import { useEffect, useState, type CSSProperties } from 'react'
import { createPortal } from 'react-dom'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/shared/auth/useAuth'
import { formatBytes, formatDateTimeInZone } from '@/shared/lib/format'
import { formatMoneyValue } from '@/shared/lib/money'
import type { Statement } from './historyApi'
import { performerLabel, kindLabel } from './historyLabels'
import { STATEMENT_PRINT_CSS, STATEMENT_PRINT_ROOT } from './statementPrint'

const cell: CSSProperties = { border: '0.2mm solid #999', padding: '1.2mm 1.6mm', verticalAlign: 'top' }
const head: CSSProperties = { ...cell, background: '#f0f0f0', fontWeight: 600, textAlign: 'start' }
const num: CSSProperties = { ...cell, textAlign: 'end', whiteSpace: 'nowrap' }

const day = (iso: string) => dayjs(iso).format('DD.MM.YYYY')

export function StatementSheet({ statement }: { statement: Statement }) {
  const { t, i18n } = useTranslation()
  const { user } = useAuth()
  const [logoBroken, setLogoBroken] = useState(false)
  const companyName = statement.company.name ?? user?.tenant.name ?? null
  const at = (iso: string) => formatDateTimeInZone(iso, statement.timezone)

  return (
    <div
      dir={i18n.dir()}
      style={{ fontFamily: 'Arial, "DejaVu Sans", sans-serif', fontSize: '9pt', color: '#000', background: '#fff' }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8mm', alignItems: 'flex-start' }}>
        <div>
          {statement.company.logoUrl && !logoBroken ? (
            <img
              src={statement.company.logoUrl}
              alt=""
              onError={() => setLogoBroken(true)}
              style={{ display: 'block', maxHeight: '18mm', maxWidth: '60mm', marginBottom: '2mm' }}
            />
          ) : null}
          {companyName ? <div style={{ fontWeight: 700, fontSize: '12pt' }}>{companyName}</div> : null}
        </div>
        <div style={{ textAlign: 'end' }}>
          <div style={{ fontSize: '17pt', fontWeight: 700 }}>{t('subscriber.history.statement.title')}</div>
          <div>
            {t('subscriber.history.statement.period', { from: day(statement.from), to: day(statement.to) })}
          </div>
          <div style={{ color: '#555' }}>
            {t('subscriber.history.statement.generated', { at: at(statement.generatedAt) })}
          </div>
        </div>
      </div>

      <table style={{ marginTop: '5mm', borderCollapse: 'collapse' }}>
        <tbody>
          <tr>
            <td style={{ paddingInlineEnd: '4mm', color: '#555' }}>{t('subscriber.history.statement.customer')}</td>
            <td style={{ fontWeight: 600 }}>
              <bdi>{statement.customer.name ?? statement.customer.login}</bdi>
            </td>
          </tr>
          <tr>
            <td style={{ paddingInlineEnd: '4mm', color: '#555' }}>{t('subscriber.history.statement.login')}</td>
            <td>
              <bdi>{statement.customer.login}</bdi>
            </td>
          </tr>
          {statement.customer.customerId ? (
            <tr>
              <td style={{ paddingInlineEnd: '4mm', color: '#555' }}>
                {t('subscriber.history.statement.customerId')}
              </td>
              <td>
                <bdi>{statement.customer.customerId}</bdi>
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>

      <table style={{ width: '100%', marginTop: '4mm', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={head}>{t('subscriber.history.columns.at')}</th>
            <th style={head}>{t('subscriber.history.columns.operation')}</th>
            <th style={{ ...head, textAlign: 'end' }}>{t('subscriber.history.columns.amount')}</th>
            <th style={{ ...head, textAlign: 'end' }}>{t('subscriber.history.columns.balanceAfter')}</th>
            <th style={head}>{t('subscriber.history.columns.reference')}</th>
            <th style={head}>{t('subscriber.history.columns.actor')}</th>
          </tr>
        </thead>
        <tbody>
          <tr style={{ fontWeight: 600 }}>
            <td style={cell} colSpan={3}>
              {t('subscriber.history.opening')}
            </td>
            <td style={num}>{formatMoneyValue(statement.opening)}</td>
            <td style={cell} colSpan={2} />
          </tr>
          {statement.rows.map((row) => (
            <tr key={row.id}>
              <td style={{ ...cell, whiteSpace: 'nowrap' }}>{at(row.at)}</td>
              <td style={cell}>
                {kindLabel(t, row)}
                {row.packageName ? <div>{row.packageName}</div> : null}
                {row.trafficBytes > 0 ? <div style={{ color: '#555' }}>+{formatBytes(row.trafficBytes)}</div> : null}
                {row.note ? <div style={{ color: '#555' }}>{row.note}</div> : null}
              </td>
              <td style={num}>{formatMoneyValue(row.amount)}</td>
              <td style={num}>{formatMoneyValue(row.balanceAfter)}</td>
              <td style={cell}>
                <bdi>{row.reference ?? ''}</bdi>
              </td>
              <td style={cell}>
                <bdi>{performerLabel(t, row)}</bdi>
              </td>
            </tr>
          ))}
          <tr style={{ fontWeight: 700 }}>
            <td style={cell} colSpan={3}>
              {t('subscriber.history.closing')}
            </td>
            <td style={num}>{formatMoneyValue(statement.closing)}</td>
            <td style={cell} colSpan={2} />
          </tr>
        </tbody>
      </table>

      {statement.truncated ? (
        <p style={{ marginTop: '3mm', fontWeight: 600 }}>{t('subscriber.history.statement.truncated')}</p>
      ) : null}
    </div>
  )
}

export function StatementPrintPortal({ statement }: { statement: Statement }) {
  useEffect(() => {
    const style = document.createElement('style')
    style.textContent = STATEMENT_PRINT_CSS
    document.head.appendChild(style)
    return () => style.remove()
  }, [])

  return createPortal(
    <div className={STATEMENT_PRINT_ROOT}>
      <StatementSheet statement={statement} />
    </div>,
    document.body,
  )
}
